/**
 * Static editorial data for the Seven-Year Cycle feature.
 * Source: "The Future CEO" series by Sean Grobbelaar.
 */

export const SHEMITAH_START = new Date(2028, 8, 1); // September 1, 2028

/** Whole months between now and the Shemitah window opening (never negative). */
export function monthsToShemitah(from: Date = new Date()): number {
  const months =
    (SHEMITAH_START.getFullYear() - from.getFullYear()) * 12 +
    (SHEMITAH_START.getMonth() - from.getMonth());
  return Math.max(0, months);
}

/** Cycle phase segments of the gauge arc (fractions must sum to 1). */
export const CYCLE_SEGMENTS = [
  { key: "reset", label: "Reset", fraction: 0.15, color: "var(--cycle-coral-hex)" },
  { key: "build", label: "Build", fraction: 0.3, color: "var(--cycle-teal-hex)" },
  { key: "harvest", label: "Harvest", fraction: 0.3, color: "var(--cycle-amber-hex)" },
  { key: "fortress", label: "Fortress", fraction: 0.25, color: "var(--cycle-coral-hex)" },
] as const;

/** Current position along the arc (0 = 2022, 1 = 2029). */
export const CURRENT_POSITION = 0.55;

export const TIMELINE = [
  { year: "1987", text: "Black Monday — markets lose 33% in a single day", next: false },
  { year: "1994", text: "Bond market collapse — Fed raises rates six times", next: false },
  { year: "2001", text: "Dot-com bust and September 11 — Nasdaq loses 78%", next: false },
  { year: "2008", text: "Lehman Brothers collapses — US stocks lose half their value", next: false },
  { year: "2015", text: "European debt crisis — Greek bailout, Chinese crash", next: false },
  { year: "2022", text: "Inflation shock — fastest rate hikes in 40 years", next: false },
  { year: "2029", text: "Next projected correction window", next: true },
];

export type PhaseCard = {
  name: string;
  dates: string;
  years: string;
  colorVar: string;
  current?: boolean;
  actions: { label: string; text: string }[];
};

export const PHASES: PhaseCard[] = [
  {
    name: "Build",
    dates: "July 2026 – June 2027",
    years: "Years 3–4",
    colorVar: "var(--cycle-teal-hex)",
    current: true,
    actions: [
      { label: "AUDIT", text: "Map every fixed commitment. Know your burn rate if revenue drops 30%." },
      { label: "NEGOTIATE", text: "Every new lease or contract gets a break clause by Q2 2028." },
      { label: "INVEST", text: "Make your biggest capital moves now. Prices are rational, financing is cheap." },
      { label: "RESERVE", text: "Start moving 5–10% of gross revenue into a cycle reserve account." },
    ],
  },
  {
    name: "Harvest",
    dates: "July 2027 – June 2028",
    years: "Years 5–6",
    colorVar: "var(--cycle-amber-hex)",
    actions: [
      { label: "COLLECT", text: "Chase every receivable. Tighten payment terms. Convert revenue to cash." },
      { label: "CUT", text: "Audit every cost line. If it doesn't generate revenue, cut it." },
      { label: "CONTRACTS", text: "Renegotiate for shorter terms or month-to-month." },
      { label: "DEBT", text: "Stop borrowing. Fix variable rates. Refinance before 2028." },
    ],
  },
  {
    name: "Fortress",
    dates: "July 2028 – December 2029",
    years: "Years 6–7 (Shemitah window)",
    colorVar: "var(--cycle-coral-hex)",
    actions: [
      { label: "TIGHTEN", text: "Operating costs at minimum. Marketing shifts to retention only." },
      { label: "COMMUNICATE", text: "Transparency with team, suppliers, and bank buys goodwill." },
      { label: "HOLD", text: "Hold your nerve. The market is resetting, not ending." },
      { label: "WATCH", text: "Build a target list of distressed assets. Don't buy yet — wait for the trough." },
    ],
  },
  {
    name: "Hunt",
    dates: "Early 2030",
    years: "Year 1 of next cycle",
    colorVar: "var(--cycle-green-hex)",
    actions: [
      { label: "ACQUIRE", text: "Distressed assets at 40–60 cents on the dollar." },
      { label: "HIRE", text: "The best talent is available. People who were untouchable in Year 5 are looking for stability." },
      { label: "BUILD", text: "Launch the projects you shelved. The runway is seven years long." },
    ],
  },
];

export const CRYPTO_PLAYBOOK = [
  {
    dates: "Q3 2026 – Q2 2027",
    name: "Accumulate",
    colorVar: "var(--cycle-teal-hex)",
    bullets: [
      "DCA into Bitcoin, not altcoins",
      "Target: $50K–$70K accumulation range",
      "Allocate 40–50% of intended total crypto position",
    ],
  },
  {
    dates: "Q3 2027 – Q2 2028",
    name: "Ride and harvest",
    colorVar: "var(--cycle-amber-hex)",
    bullets: [
      "Hold position through recovery",
      "Set exit prices now: scale out 10–15% per $25K above $150K",
      "Watch the Fed — rate hikes are your early warning",
    ],
  },
  {
    dates: "Q3 2028 – Q4 2028",
    name: "Exit",
    colorVar: "var(--cycle-coral-hex)",
    bullets: [
      "Reduce to 20–30% of peak holdings by September 2028",
      "Move to stablecoins or fiat, not altcoins",
      "Ignore the halving narrative — it already failed once",
    ],
  },
  {
    dates: "Q1 2029 – Q4 2029",
    name: "Wait",
    colorVar: "hsl(var(--muted-foreground))",
    bullets: [
      "Do nothing. Watch. Build your re-entry target list.",
      "If BTC hit $200K pre-correction, a 70% drawdown = $60K target zone",
    ],
  },
  {
    dates: "Q1 2030 – Q4 2030",
    name: "Reload",
    colorVar: "var(--cycle-green-hex)",
    bullets: [
      "Deploy capital aggressively — this is the generational entry",
      "Allocate 60–80% of position in first six months after bottom",
      "Hold through Years 1–4. Next ATH window: 2032–2033",
    ],
  },
];
